import { toast } from 'sonner';
import { isEqual } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientMenuCategoryDto, ClientMenuItemDto, ServiceTypeEnum } from '@/app/store/api/restApi';
import {
  changeTotalCheck,
  changeUnavailableProductIds,
  changeProductPrice,
  removeFromBasket,
  useAppDispatch,
  useAppSelector,
} from '@/app/store';

const isMenuItemUnavailable = (menuItem: ClientMenuItemDto, isCdekTemporary: boolean) =>
  menuItem.isWithinDisplayInterval === false || isCdekTemporary;

export const useActualiseBasket = ({
  menuItems,
  menuItemsLoading,
  categories,
}: {
  menuItems: ClientMenuItemDto[];
  menuItemsLoading: boolean;
  categories: ClientMenuCategoryDto[];
}) => {
  const { t } = useTranslation('errors');
  const dispatch = useAppDispatch();
  const { products, unavailableProductIds } = useAppSelector((state) => state.basket);
  const deliveryType = useAppSelector((state) => state.order.deliveryType);
  const isCdek = deliveryType === ServiceTypeEnum.OrderPoint;

  // A "temporary category" is one with a display window; under CDEK its items are
  // unavailable. Detected by category membership, not the item's current-window flag.
  const temporaryCategoryIds = useMemo(
    () => new Set(categories.filter((c) => c.displayInterval).map((c) => c.id)),
    [categories],
  );

  const actualiseUnavailableProduct = ({
    product,
    nextUnavailableProductIds,
  }: {
    product: ClientMenuItemDto;
    nextUnavailableProductIds: string[];
  }) => {
    if (isMenuItemUnavailable(product, isCdek && temporaryCategoryIds.has(product.categoryId))) {
      nextUnavailableProductIds.push(product.id);
      if (!unavailableProductIds.includes(product.id)) {
        dispatch(changeTotalCheck({ productId: product.id, operation: 'subtract' }));
      }
    } else if (unavailableProductIds.includes(product.id)) {
      dispatch(changeTotalCheck({ productId: product.id, operation: 'add' }));
    }
  };

  useEffect(() => {
    if (!menuItemsLoading) {
      const nextUnavailableProductIds: string[] = [];

      products.map((inBasket) => {
        const prodExists = menuItems.find((mi) => mi.id === inBasket.product.id);

        if (inBasket.product.isVariable) {
          if (
            !prodExists ||
            !prodExists.isVariable ||
            !isEqual(
              inBasket.product.variants?.map((v) => ({ id: v.id, price: v.price })),
              prodExists.variants?.map((v) => ({ id: v.id, price: v.price })),
            )
          ) {
            inBasket.variants?.forEach((pVar) =>
              dispatch(removeFromBasket({ productId: inBasket.product.id, variantId: pVar.value.id })),
            );
            toast.warning(t('messages.MENU_ITEM_NOT_FOUND'));
          } else {
            actualiseUnavailableProduct({ product: prodExists, nextUnavailableProductIds });
          }
        } else {
          if (!prodExists || prodExists.isVariable) {
            dispatch(removeFromBasket({ productId: inBasket.product.id }));
            toast.error(t('messages.MENU_ITEM_NOT_FOUND'));
          } else {
            if (prodExists.price !== inBasket.product.price) {
              dispatch(changeProductPrice({ productId: prodExists.id, price: prodExists.price ?? 0 }));
            }
            actualiseUnavailableProduct({ product: prodExists, nextUnavailableProductIds });
          }
        }
      });

      if (!isEqual(unavailableProductIds, nextUnavailableProductIds)) {
        dispatch(changeUnavailableProductIds(nextUnavailableProductIds));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, menuItems, menuItemsLoading, t, isCdek, temporaryCategoryIds]);
};
